/**
 * Blocco 2 — Password strength meter (client-side visual feedback).
 *
 * Mirrors the backend policy (min 12 chars + basic common-password check)
 * but the SERVER is always authoritative. The meter is purely UX sugar and
 * intentionally lenient about the blacklist — the definitive rejection
 * comes from the API response.
 */
import { useMemo } from "react";

const MIN_LENGTH = 12;

// Small client-side mirror of the top common passwords. Kept intentionally
// short — the meter's job is to warn early, the backend's job is to enforce.
const COMMON_MIRROR = new Set([
  "password", "passwordpassword", "password1234", "welcome", "welcome123",
  "qwerty", "qwertyuiop", "123456", "12345678", "123456789", "1234567890",
  "iloveyou", "iloveyou1234", "letmein", "letmeinplease", "admin",
  "admin123", "administrator", "recenup", "recenup123", "recenup2026",
  "ciao", "italia", "juventus", "internazionale", "amore123",
]);

function scorePassword(pw) {
  if (!pw) return { level: "empty", label: "", pct: 0, ok: false };
  const length = pw.length;
  const lower = pw.toLowerCase().replace(/[0-9!]+$/, "");
  const isCommon = COMMON_MIRROR.has(lower) || COMMON_MIRROR.has(pw.toLowerCase());
  if (length < MIN_LENGTH) {
    return {
      level: "weak",
      label: `${length}/${MIN_LENGTH} caratteri`,
      pct: Math.min(60, Math.round((length / MIN_LENGTH) * 60)),
      ok: false,
    };
  }
  if (isCommon) {
    return { level: "weak", label: "Password troppo comune", pct: 20, ok: false };
  }
  const diversity =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  if (length >= 20 && diversity >= 3) {
    return { level: "excellent", label: "Eccellente", pct: 100, ok: true };
  }
  if (length >= 16 && diversity >= 2) {
    return { level: "strong", label: "Robusta", pct: 85, ok: true };
  }
  return { level: "good", label: "Ok", pct: 70, ok: true };
}

const COLOURS = {
  empty: "#3A3A3F",
  weak: "#C6423B",
  good: "#D4A574",
  strong: "#4A8A6E",
  excellent: "#00A8AA",
};

export default function PasswordStrengthMeter({ password, testId }) {
  const info = useMemo(() => scorePassword(password || ""), [password]);
  if (!password) return null;
  return (
    <div data-testid={testId || "password-strength-meter"} className="mt-2">
      <div
        className="h-1 w-full rounded-full bg-[var(--bg-3,#1a1a1e)] overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="h-full"
          style={{
            width: `${info.pct}%`,
            backgroundColor: COLOURS[info.level],
            transition: "width 0.25s ease, background-color 0.25s ease",
          }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] font-sans">
        <span style={{ color: COLOURS[info.level] }} data-testid="password-strength-label">
          {info.label}
        </span>
        {!info.ok && (
          <span className="text-[var(--fg-4)]">
            Minimo {MIN_LENGTH} caratteri, evita password comuni
          </span>
        )}
      </div>
    </div>
  );
}
